import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext from '../../../contexts/AppContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { MultiSelect } from '../../../components/ui/multi-select';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2, RefreshCw, Eye, EyeOff, Copy } from 'lucide-react';
import { getAllPermissions, getDefaultPermissions, usePermissionLabel } from '../../../constants/permissions';
import { generateRandomPassword } from '../../../utils/passwordUtils';

export const UserCreateDialog = ({ open, onClose, onUserCreated }) => {
  const { t } = useTranslation();
  const permissionLabel = usePermissionLabel();
  const { currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDesks, setSelectedServiceDesks] = useState([]);
  const [selectedAllowedCreationDesks, setSelectedAllowedCreationDesks] = useState([]);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    mode: 'rw',
    lang: 'fr',
    serviceDeskId: '',
    sectors: '',
    permissions: [],
    allowedTicketCreationDesks: []
  });

  // Filter permission options to only show those not included in role defaults
  const defaultPermissionsForRole = getDefaultPermissions(formData.role);
  const availablePermissionOptions = getAllPermissions()
    .filter(option => !defaultPermissionsForRole.includes(option.value))
    .map(option => ({ value: option.value, label: permissionLabel(option.value) }));

  // Fetch service desks for superadmin
  useEffect(() => {
    if (open && isSuperAdmin) {
      fetchServiceDesks();
    }
  }, [open, isSuperAdmin]);

  // Initialize serviceDeskId for non-superadmin
  useEffect(() => {
    if (open && !isSuperAdmin && currentUser?.serviceDeskId) {
      const adminServiceDeskId = Array.isArray(currentUser.serviceDeskId)
        ? currentUser.serviceDeskId.join(', ')
        : String(currentUser.serviceDeskId);
      setFormData(prev => ({ ...prev, serviceDeskId: adminServiceDeskId }));
    }
  }, [open, isSuperAdmin, currentUser?.serviceDeskId]);

  const fetchServiceDesks = async () => {
    try {
      const response = await fetchWithAuth('/service-desks/all');
      const json = await response.json();
      if (json.success) {
        setServiceDesks(json.data);
      }
    } catch (err) {
      console.error('Error fetching service desks:', err);
    }
  };

  // Convert service desks to MultiSelect options format
  const serviceDeskOptions = serviceDesks.map(sd => ({
    value: sd.id,
    label: sd.name
  }));

  // Handle multi-select change
  const handleServiceDesksChange = (selected) => {
    setSelectedServiceDesks(selected);

    // Update formData.serviceDeskId
    if (selected.includes('*')) {
      setFormData(prev => ({ ...prev, serviceDeskId: '*' }));
    } else if (selected.length > 0) {
      setFormData(prev => ({ ...prev, serviceDeskId: selected.join(', ') }));
    } else {
      setFormData(prev => ({ ...prev, serviceDeskId: '' }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);

    // Reset allowed creation desks when role changes
    if (field === 'role') {
      setSelectedAllowedCreationDesks([]);
      setFormData(prev => ({ ...prev, allowedTicketCreationDesks: [] }));
    }
  };

  const handlePermissionsChange = (selected) => {
    setFormData(prev => ({ ...prev, permissions: selected }));
  };

  const handleAllowedCreationDesksChange = (selected) => {
    setSelectedAllowedCreationDesks(selected);
    setFormData(prev => ({ ...prev, allowedTicketCreationDesks: selected }));
  };

  const handleGeneratePassword = () => {
    const password = generateRandomPassword();
    handleChange('password', password);
    setShowPassword(true);
  };

  const copyPasswordToClipboard = async () => {
    if (formData.password) {
      try {
        await navigator.clipboard.writeText(formData.password);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  // Reset extra permissions when role changes (defaults are applied automatically by backend)
  useEffect(() => {
    setFormData(prev => ({ ...prev, permissions: [] }));
  }, [formData.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Parse sectors (comma-separated string to array)
      const sectorsArray = formData.sectors
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      // Parse serviceDeskId - always as array: [3], [3, 205], or ["*"]
      let serviceDeskId;
      const rawServiceDeskId = formData.serviceDeskId.trim();
      if (rawServiceDeskId === '*') {
        serviceDeskId = ['*'];
      } else if (rawServiceDeskId.includes(',')) {
        serviceDeskId = rawServiceDeskId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      } else {
        const parsed = parseInt(rawServiceDeskId);
        if (isNaN(parsed)) {
          throw new Error(t('Invalid Service Desk ID'));
        }
        serviceDeskId = [parsed];
      }

      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        mode: formData.mode,
        lang: formData.lang,
        serviceDeskId,
        sectors: sectorsArray,
        permissions: formData.permissions,
        allowedTicketCreationDesks: formData.role === 'technician' ? formData.allowedTicketCreationDesks : []
      };

      const response = await fetchWithAuth('/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || t('Creating the user failed'));
      }

      if (json.success) {
        // Reset form
        setFormData({
          username: '',
          email: '',
          password: '',
          role: 'user',
          mode: 'rw',
          lang: 'fr',
          serviceDeskId: '',
          sectors: '',
          permissions: [],
          allowedTicketCreationDesks: []
        });
        setSelectedServiceDesks([]);
        setSelectedAllowedCreationDesks([]);
        onUserCreated();
      } else {
        throw new Error(json.error || t('Creating the user failed'));
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t('Create a new user')}</DialogTitle>
          <DialogDescription>
            {t('Fill in the information to create a new user account')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('Username *')}</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('Email *')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('Password *')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  autoComplete="off"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleGeneratePassword}
                title={t('Generate a password')}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {formData.password && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={copyPasswordToClipboard}
                  title={t('Copy the password')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('Use the button to generate a secure password')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">{t('Role *')}</Label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t('User')}</SelectItem>
                  <SelectItem value="manager">{t('Manager')}</SelectItem>
                  <SelectItem value="technician">{t('Technician')}</SelectItem>
                  <SelectItem value="admin">{t('Admin')}</SelectItem>
                  <SelectItem value="superadmin">{t('Super Admin')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mode">{t('Mode *')}</Label>
              <Select value={formData.mode} onValueChange={(value) => handleChange('mode', value)}>
                <SelectTrigger id="mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rw">{t('Read / Write')}</SelectItem>
                  <SelectItem value="r">{t('Read only')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lang">{t('Language')}</Label>
            <Select value={formData.lang} onValueChange={(value) => handleChange('lang', value)}>
              <SelectTrigger id="lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 French</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="es">🇪🇸 Spanish</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('Service Desk *')}</Label>
            {isSuperAdmin ? (
              <>
                <MultiSelect
                  options={serviceDeskOptions}
                  selected={selectedServiceDesks}
                  onChange={handleServiceDesksChange}
                  placeholder={t('Select the Service Desks…')}
                  allOption={{ value: '*', label: t('All Service Desks') }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('Select one or more Service Desks, or "All" to grant access to all')}
                </p>
              </>
            ) : (
              <>
                <Input
                  id="serviceDeskId"
                  value={formData.serviceDeskId}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-muted-foreground">
                  {t('The user will be created with your Service Desk')}
                </p>
              </>
            )}
          </div>

          {formData.role === 'technician' && isSuperAdmin && (
            <div className="space-y-2">
              <Label>{t('Allowed service desks for ticket creation')}</Label>
              <MultiSelect
                options={serviceDeskOptions}
                selected={selectedAllowedCreationDesks}
                onChange={handleAllowedCreationDesksChange}
                placeholder={t('Select the Service Desks…')}
              />
              <p className="text-xs text-muted-foreground">
                {t('Select the Service Desks on which this technician can create tickets')}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="sectors">{t('Sectors')}</Label>
            <Textarea
              id="sectors"
              value={formData.sectors}
              onChange={(e) => handleChange('sectors', e.target.value)}
              placeholder={t('Ex: Sector A, Sector B, Sector C')}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {t('Enter the sectors separated by commas')}
            </p>
          </div>

          {currentUser?.role === 'superadmin' && (
            <div className="space-y-2">
              <Label>{t('Additional permissions')}</Label>
              {availablePermissionOptions.length > 0 ? (
                <MultiSelect
                  options={availablePermissionOptions}
                  selected={formData.permissions}
                  onChange={handlePermissionsChange}
                  placeholder={t('Select additional permissions…')}
                />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {t('This role already has all available permissions.')}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('Default permissions for the role')} "{formData.role}" : {defaultPermissionsForRole.length > 0
                  ? defaultPermissionsForRole.join(', ')
                  : t('none')}
              </p>
            </div>
          )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('Create the user')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
